import { Checkbox } from "primereact/checkbox";
import { OverlayPanel } from "primereact/overlaypanel";
import { useRef, useState } from "react";
import styled from "styled-components";

import {
  FilterPanelStyled,
  FilterTriggerStyled,
  FilterValueStyled,
} from "./styled";

const OptionLabelStyled = styled.label`
  cursor: pointer;

  color: var(--brand-gray-700);
  font-size: 1rem;
`;

const OptionDividerStyled = styled.div`
  border-top: 1px solid var(--brand-gray-200);
`;

export interface ICheckboxFilterOption {
  value: string;
  name: string;
}

export function CheckboxFilter({
  label,
  allLabel,
  options,
  value,
  onChange,
  testIdPrefix,
  tourAnchor,
}: {
  label: string;
  allLabel: string;
  options: ICheckboxFilterOption[];
  value: string[];
  onChange: (values: string[]) => void;
  testIdPrefix: string;
  /** CLE-3015 — a `TourAnchor` value for the trigger (`data-tour`). */
  tourAnchor?: string;
}) {
  const panel = useRef<OverlayPanel>(null);
  const [open, setOpen] = useState(false);

  const selected = value ?? [];

  const selectionLabel = (() => {
    if (selected.length === 0) return allLabel;
    if (selected.length === 1) {
      const option = options.find((item) => item.value === selected[0]);
      return option?.name ?? "1 selected";
    }
    return `${selected.length} selected`;
  })();

  const toggle = (optionValue: string) => {
    onChange(
      selected.includes(optionValue)
        ? selected.filter((item) => item !== optionValue)
        : [...selected, optionValue]
    );
  };

  return (
    <>
      <FilterTriggerStyled
        type="button"
        data-testid={`${testIdPrefix}_multiselect_OrderTable`}
        data-tour={tourAnchor}
        className="flex align-items-center gap-2"
        onClick={(e) => {
          panel.current?.toggle(e);
          setOpen((wasOpen) => !wasOpen);
        }}
      >
        {label}
        <FilterValueStyled>{selectionLabel}</FilterValueStyled>
        <i className={open ? "pi pi-chevron-up" : "pi pi-chevron-down"} />
      </FilterTriggerStyled>

      <FilterPanelStyled ref={panel} onHide={() => setOpen(false)}>
        <div className="flex align-items-center gap-3 pb-3">
          <Checkbox
            inputId={`${testIdPrefix}FilterAll`}
            data-testid={`${testIdPrefix}_any_OrderTable`}
            checked={selected.length === 0}
            onChange={() => onChange([])}
          />
          <OptionLabelStyled htmlFor={`${testIdPrefix}FilterAll`}>
            {allLabel}
          </OptionLabelStyled>
        </div>

        <OptionDividerStyled />

        {options.map((option) => (
          <div
            key={option.value}
            className="flex align-items-center gap-3 pt-3"
          >
            <Checkbox
              inputId={`${testIdPrefix}Filter-${option.value}`}
              data-testid={`${testIdPrefix}_${option.value}_OrderTable`}
              checked={selected.includes(option.value)}
              onChange={() => toggle(option.value)}
            />
            <OptionLabelStyled
              htmlFor={`${testIdPrefix}Filter-${option.value}`}
            >
              {option.name}
            </OptionLabelStyled>
          </div>
        ))}
      </FilterPanelStyled>
    </>
  );
}
